import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getPlans, updatePlan } from '../utils/api';
import { Check, X, Edit2, Save, ChevronDown, ChevronUp } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────

const PLAN_META = {
  trial:      { label: 'Free Trial',       color: '#64748b', light: '#f1f5f9', badge: '#e2e8f0' },
  early_bird: { label: 'Early Bird',  color: '#059669', light: '#ecfdf5', badge: '#d1fae5' },
  core:       { label: 'Zeus Core',        color: '#2563eb', light: '#eff6ff', badge: '#dbeafe' },
  pro:        { label: 'Zeus Pro',         color: '#b45309', light: '#fffbeb', badge: '#fef3c7' },
  elite:      { label: 'Zeus Elite',       color: '#7c3aed', light: '#f5f3ff', badge: '#ede9fe' },
};

const FEATURES = [
  { key: 'case_management',    label: 'Case Management'        },
  { key: 'task_management',    label: 'Task Management'        },
  { key: 'calendar',           label: 'Calendar & Events'      },
  { key: 'intake_forms',       label: 'Client Intake Forms'    },
  { key: 'messenger',          label: 'Messenger'              },
  { key: 'care_providers',     label: 'Provider Network'       },
  { key: 'document_templates', label: 'Document Templates'     },
  { key: 'billing',            label: 'Billing & Invoicing'    },
  { key: 'letters_forms',      label: 'Letters & Forms'        },
  { key: 'reports',            label: 'Reports & Analytics'    },
  { key: 'workflows',          label: 'Workflows & Automation' },
  { key: 'ocr',                label: 'OCR Document Reader'    },
  { key: 'api_access',         label: 'API Access'             },
  { key: 'google_maps',        label: 'Google Maps / Places'   },
  { key: 'fax_plus',          label: 'Fax.plus Integration'   },
  { key: 'ringcentral',       label: 'RingCentral Integration' },
  { key: 'document_signing',  label: 'DocuSeal'              },
];

const REPORTS_OPTS  = ['basic', 'standard', 'advanced'];
const SUPPORT_OPTS  = ['email', 'priority', 'dedicated'];
const SUPPORT_LABEL = { email: 'Email only', priority: 'Priority + onboarding call', dedicated: 'Dedicated account manager' };
const WORKFLOW_HINT = { '-1': 'Unlimited', '0': 'None' };

// ── Helpers ───────────────────────────────────────────────────────────────────

function toast(msg, ok = true) {
  const el = document.createElement('div');
  el.textContent = msg;
  Object.assign(el.style, {
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem',
    background: ok ? '#22c55e' : '#ef4444', color: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
  });
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

// ── Edit Drawer ───────────────────────────────────────────────────────────────

// Defined outside EditDrawer so React doesn't remount inputs on every keystroke
function DrawerRow({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
      <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function EditDrawer({ plan, onClose, onSaved }) {
  const [d, setD]       = useState(null);
  const [saving, setSav] = useState(false);

  useEffect(() => {
    // Always use the name stored in the DB — don't override with PLAN_META labels
    setD({
      name:                  plan.name || '',
      price_per_seat_annual: plan.price_per_seat_annual ?? 0,
      price_per_seat_mtm:    plan.price_per_seat_mtm    ?? 0,
      max_seats_per_seat:    plan.max_seats_per_seat    ?? '',
      bundle_small_annual:   plan.bundle_small_annual   ?? '',
      bundle_mid_annual:     plan.bundle_mid_annual     ?? '',
      workflow_limit:        plan.workflow_limit         ?? 0,
      reports_tier:          plan.reports_tier           || 'basic',
      has_ocr:               plan.has_ocr               || false,
      has_api:               plan.has_api               || false,
      has_google_maps:       plan.has_google_maps        || false,
      has_fax_plus:          plan.has_fax_plus           || false,
      has_ringcentral:       plan.has_ringcentral         || false,
      has_document_signing:  plan.has_document_signing    || false,
      support_level:         plan.support_level         || 'email',
      onboarding_sessions:   plan.onboarding_sessions   ?? 0,
      highlight:             plan.highlight             || false,
      highlight_label:       plan.highlight_label       || '',
      is_active:             plan.is_active             !== false,
      is_internal:           plan.is_internal           || false,
      trial_days:            (plan.slug === 'trial' || plan.slug === 'trialing') ? (plan.trial_days ?? 14) : undefined,
      features: {
        ...plan.features,
        // Sync has_ocr/has_api flags into features object so the toggles are accurate
        ocr:         plan.has_ocr         || plan.features?.ocr         || false,
        api_access:  plan.has_api         || plan.features?.api_access  || false,
        google_maps:  plan.has_google_maps  || plan.features?.google_maps  || false,
        fax_plus:     plan.has_fax_plus     || plan.features?.fax_plus     || false,
        ringcentral:  plan.has_ringcentral  || plan.features?.ringcentral  || false,
        document_signing: plan.has_document_signing || plan.features?.document_signing || false,
      },
    });
  }, [plan]);

  if (!d) return null;

  const meta = PLAN_META[plan.slug] || PLAN_META.core;

  const set = (key, val) => setD(p => ({ ...p, [key]: val }));
  const setFeat = key => setD(p => {
    const newVal = !p.features[key];
    const extra = {};
    if (key === 'ocr')         extra.has_ocr         = newVal;
    if (key === 'api_access')  extra.has_api         = newVal;
    if (key === 'google_maps') extra.has_google_maps = newVal;
    if (key === 'fax_plus')    extra.has_fax_plus     = newVal;
    if (key === 'ringcentral') extra.has_ringcentral  = newVal;
    if (key === 'document_signing') extra.has_document_signing = newVal;
    return { ...p, features: { ...p.features, [key]: newVal }, ...extra };
  });

  const save = async () => {
    setSav(true);
    try {
      const payload = {
        ...d,
        price_per_seat_annual: parseFloat(d.price_per_seat_annual) || 0,
        price_per_seat_mtm:    parseFloat(d.price_per_seat_mtm)    || 0,
        max_seats_per_seat:    d.max_seats_per_seat !== '' ? parseInt(d.max_seats_per_seat)    : null,
        bundle_small_annual:   d.bundle_small_annual !== '' ? parseFloat(d.bundle_small_annual) : null,
        bundle_mid_annual:     d.bundle_mid_annual   !== '' ? parseFloat(d.bundle_mid_annual)   : null,
        workflow_limit:        parseInt(d.workflow_limit),
        onboarding_sessions:   parseInt(d.onboarding_sessions) || 0,
        ...(plan.slug === 'trial' || plan.slug === 'trialing'
          ? { trial_days: parseInt(d.trial_days) || 14 }
          : {}),
      };
      const updated = await updatePlan(plan.id, payload);
      toast('Plan saved');
      onSaved(updated);
      onClose();
    } catch (err) {
      toast(err.message || 'Save failed', false);
    } finally { setSav(false); }
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200 }} />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, zIndex: 201,
        background: 'var(--bg-elevated)', borderLeft: '1px solid var(--border)',
        overflowY: 'auto', display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
      }}>
        {/* Drawer header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          background: meta.light.replace('#', 'color-mix(in srgb, ') ,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 1,
          background: 'var(--bg-elevated)',
          borderBottom: `3px solid ${meta.color}`,
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: meta.color, letterSpacing: '0.06em', marginBottom: 2 }}>
              Editing
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              {plan.name || meta.label}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>

        <div style={{ padding: '20px', flex: 1 }}>

          {/* Plan name */}
          <DrawerRow label="Plan Name">
            <input className="form-input" value={d.name} onChange={e => set('name', e.target.value)} />
          </DrawerRow>

          {/* Trial Period — only shown for the Free Trial plan */}
          {(plan.slug === 'trial' || plan.slug === 'trialing') && (
            <>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 14 }}>
                Trial Period
              </div>
              <DrawerRow label="Trial Length (days)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="number" min={1} max={365} className="form-input"
                    value={d.trial_days}
                    onChange={e => set('trial_days', e.target.value)}
                    style={{ width: 90 }}
                  />
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    days after activation
                  </span>
                </div>
              </DrawerRow>
              <div style={{
                padding: '8px 12px', borderRadius: 7, marginBottom: 14,
                background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
                fontSize: '0.8rem', color: 'var(--blue)', lineHeight: 1.5,
              }}>
                 When this plan is applied to an organization, <strong>trial_ends_at</strong> will be
                set to <strong>{d.trial_days || 14} days</strong> from the activation date.
                The trial countdown appears in the firm's Subscription settings.
              </div>
            </>
          )}

          {/* ── Pricing ── */}
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 14 }}>
            Pricing
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 0 }}>
            <DrawerRow label="Annual ($/seat/mo)">
              <input type="number" min={0} className="form-input" value={d.price_per_seat_annual}
                onChange={e => set('price_per_seat_annual', e.target.value)} />
            </DrawerRow>
            <DrawerRow label="Month-to-Month ($/seat/mo)">
              <input type="number" min={0} className="form-input" value={d.price_per_seat_mtm}
                onChange={e => set('price_per_seat_mtm', e.target.value)} />
            </DrawerRow>
          </div>

          <DrawerRow label="Max Seats per-seat pricing (blank = unlimited)">
            <input type="number" min={1} className="form-input" placeholder="Unlimited"
              value={d.max_seats_per_seat} onChange={e => set('max_seats_per_seat', e.target.value)} />
          </DrawerRow>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <DrawerRow label="Small Firm Bundle — 5 seats ($/mo)">
              <input type="number" min={0} className="form-input" placeholder="N/A"
                value={d.bundle_small_annual} onChange={e => set('bundle_small_annual', e.target.value)} />
            </DrawerRow>
            <DrawerRow label="Mid Firm Bundle — 10 seats ($/mo)">
              <input type="number" min={0} className="form-input" placeholder="N/A"
                value={d.bundle_mid_annual} onChange={e => set('bundle_mid_annual', e.target.value)} />
            </DrawerRow>
          </div>

          {/* ── Capabilities ── */}
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 14, marginTop: 8 }}>
            Capabilities
          </div>

          <DrawerRow label="Workflows (0 = none, -1 = unlimited, or enter a number)">
            <input type="number" min={-1} className="form-input" value={d.workflow_limit}
              onChange={e => set('workflow_limit', e.target.value)} />
          </DrawerRow>

          <DrawerRow label="Reports Tier">
            <select className="form-select" value={d.reports_tier} onChange={e => set('reports_tier', e.target.value)}>
              {REPORTS_OPTS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
            </select>
          </DrawerRow>

          <DrawerRow label="Support Level">
            <select className="form-select" value={d.support_level} onChange={e => set('support_level', e.target.value)}>
              {SUPPORT_OPTS.map(o => <option key={o} value={o}>{SUPPORT_LABEL[o]}</option>)}
            </select>
          </DrawerRow>

          <DrawerRow label="Onboarding Sessions">
            <input type="number" min={0} className="form-input" value={d.onboarding_sessions}
              onChange={e => set('onboarding_sessions', e.target.value)} />
          </DrawerRow>

          {/* ── Features ── */}
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 10, marginTop: 8 }}>
            Features (click to toggle)
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
            {FEATURES.map(feat => {
              const on = d.features?.[feat.key];
              return (
                <button key={feat.key} onClick={() => setFeat(feat.key)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  borderRadius: 7, border: `1px solid ${on ? meta.color + '40' : 'var(--border)'}`,
                  background: on ? meta.color + '10' : 'transparent',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: on ? meta.color : 'var(--bg-base)',
                    border: `2px solid ${on ? meta.color : 'var(--border)'}`,
                    transition: 'all 0.12s',
                  }}>
                    {on && <Check size={11} color="#fff" strokeWidth={3} />}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: on ? 600 : 400, color: on ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {feat.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Display ── */}
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 14 }}>
            Display
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', cursor: 'pointer', marginBottom: 10, userSelect: 'none' }}>
            <input type="checkbox" checked={d.highlight} onChange={e => set('highlight', e.target.checked)}
              style={{ width: 15, height: 15, accentColor: 'var(--gold)' }} />
            Show "Most Popular" badge
          </label>

          {d.highlight && (
            <DrawerRow label="Badge label (e.g. Most Popular)">
              <input className="form-input" value={d.highlight_label}
                onChange={e => set('highlight_label', e.target.value)} placeholder="Most Popular" />
            </DrawerRow>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', cursor: 'pointer', marginBottom: 20, userSelect: 'none' }}>
            <input type="checkbox" checked={d.is_active} onChange={e => set('is_active', e.target.checked)}
              style={{ width: 15, height: 15, accentColor: meta.color }} />
            Active (visible to customers)
          </label>
        </div>

        {/* Sticky footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, background: 'var(--bg-elevated)', position: 'sticky', bottom: 0 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
            {saving
              ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Saving…</>
              : <><Save size={13} /> Save Changes</>}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PlansPage() {
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // plan object
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans(await getPlans()); }
    catch { toast('Failed to load plans', false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (updated) => {
    setPlans(prev => prev.map(p => p.id === updated.id ? updated : p));
  };

  const wfLabel = (n) => {
    if (n === -1) return 'Unlimited';
    if (n === 0)  return 'None';
    return `Up to ${n}`;
  };

  if (loading) return (
    <Layout title="Plans">
      <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading plans…
      </div>
    </Layout>
  );

  // ── Feature matrix rows ───────────────────────────────────────────────────
  const PREVIEW_ROWS = 6;
  const matrixRows = [
    ...FEATURES,
    { key: '_wf',      label: 'Workflows',         render: p => wfLabel(p.workflow_limit)                                                     },
    { key: '_reports', label: 'Reports',            render: p => (p.reports_tier || 'basic').charAt(0).toUpperCase() + (p.reports_tier || 'basic').slice(1) },

    { key: '_support', label: 'Support',            render: p => SUPPORT_LABEL[p.support_level] || p.support_level || '—'                     },
    { key: '_onboard', label: 'Onboarding',         render: p => p.onboarding_sessions > 0 ? `${p.onboarding_sessions} session${p.onboarding_sessions > 1 ? 's' : ''}` : '—' },
  ];

  const visibleRows = showAll ? matrixRows : matrixRows.slice(0, PREVIEW_ROWS);

  return (
    <Layout title="Plans">

      {/* ── Plan Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        {plans.map(plan => {
          const meta = PLAN_META[plan.slug] || { label: plan.name, color: '#64748b', light: '#f8fafc', badge: '#e2e8f0' };
          const price = plan.price_per_seat_annual;
          return (
            <div key={plan.id} style={{
              borderRadius: 12,
              border: `2px solid ${meta.color}30`,
              overflow: 'hidden',
            }}>
              {/* Coloured top bar */}
              <div style={{ background: meta.color, padding: '14px 16px' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>
                  {plan.slug}
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>{plan.name || meta.label}</div>
                {plan.is_internal && (
            <span style={{
              display:'inline-flex', alignItems:'center', gap:4,
              padding:'2px 8px', borderRadius:10, fontSize:'0.62rem', fontWeight:700,
              background:'rgba(245,158,11,0.15)', color:'var(--amber)',
              border:'1px solid rgba(245,158,11,0.25)', marginBottom:6,
            }}>
              INTERNAL INTERNAL
            </span>
          )}
          {plan.highlight && (
                  <div style={{ display: 'inline-block', marginTop: 4, background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '0.62rem', fontWeight: 700, padding: '1px 7px', borderRadius: 20, letterSpacing: '0.04em' }}>
                    {plan.highlight_label || 'Most Popular'}
                  </div>
                )}
              </div>

              {/* Pricing + edit */}
              <div style={{ padding: '12px 16px', background: 'var(--bg-elevated)' }}>
                <div style={{ marginBottom: 10 }}>
                  <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {price === 0 ? 'Free' : `$${price}`}
                  </span>
                  {price > 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 3 }}>/seat/mo</span>}
                  {price > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Billed annually · ${plan.price_per_seat_mtm}/mo m-t-m</div>}
                </div>

                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
                  {plan.max_seats_per_seat ? `Up to ${plan.max_seats_per_seat} seats` : 'Unlimited seats'}
                  {plan.bundle_small_annual ? ` · $${plan.bundle_small_annual}/mo (5 seats)` : ''}
                </div>

                {!plan.is_active && (
                  <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600, marginBottom: 8 }}>Inactive — hidden from customers</div>
                )}

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditing(plan)}
                  style={{ width: '100%', justifyContent: 'center', borderColor: meta.color + '50', color: meta.color }}>
                  <Edit2 size={12} /> Edit {plan.name || meta.label}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Feature Matrix Table ── */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-header">
          <span className="card-title">Feature Comparison</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Live view — click Edit on a plan above to change anything</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', width: '36%', fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Feature
                </th>
                {plans.map(plan => {
                  const meta = PLAN_META[plan.slug] || { label: plan.name, color: '#64748b' };
                  return (
                    <th key={plan.id} style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 800, color: meta.color, fontSize: '0.88rem' }}>
                      {plan.name || meta.label}
                    </th>
                  );
                })}
              </tr>

              {/* Pricing sub-header */}
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-base)' }}>
                <td style={{ padding: '8px 16px', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                  Price per seat / mo (annual)
                </td>
                {plans.map(plan => (
                  <td key={plan.id} style={{ padding: '8px 16px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                    {plan.price_per_seat_annual === 0 ? 'Free' : `$${plan.price_per_seat_annual}`}
                  </td>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((row, ri) => {
                const isFeature = !row.key.startsWith('_');
                return (
                  <tr key={row.key} style={{ borderBottom: '1px solid var(--border)', background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ padding: '9px 16px', color: 'var(--text-primary)', fontWeight: 500 }}>
                      {row.label}
                    </td>
                    {plans.map(plan => {
                      const meta = PLAN_META[plan.slug] || { color: '#64748b' };
                      let cell;
                      if (row.render) {
                        cell = row.render(plan);
                      } else {
                        const on = plan.features?.[row.key];
                        cell = on
                          ? <Check size={15} style={{ color: meta.color }} strokeWidth={2.5} />
                          : <span style={{ color: 'var(--border)', fontSize: '1rem', lineHeight: 1 }}>—</span>;
                      }
                      return (
                        <td key={plan.id} style={{ padding: '9px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Show more / less toggle */}
        <button
          onClick={() => setShowAll(s => !s)}
          style={{
            width: '100%', padding: '11px', border: 'none', borderTop: '1px solid var(--border)',
            background: 'var(--bg-base)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
            color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          {showAll
            ? <><ChevronUp size={13} /> Show less</>
            : <><ChevronDown size={13} /> Show all {matrixRows.length} rows</>}
        </button>
      </div>

      {/* Edit Drawer */}
      {editing && (
        <EditDrawer
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </Layout>
  );
}
